<script setup lang="ts">
import { CssVarColors } from "@/shared/themes/CssVarColors.interface";
import type { PageBlock } from "@/views/blocks/PageBlock.interface";
import EditableElement from "@/views/page/EditableElement.vue";
import { computed } from "vue";
import type { SplitWithImageProps } from "./SplitWithImageProps";

// Import shared components
import Section from "@/views/blocks/shared/section/layout/section/Section.vue";
import Image from "@/views/blocks/shared/section/media/image/Image.vue";
import Heading from "@/views/blocks/shared/section/typography/heading/Heading.vue";
import Paragraph from "@/views/blocks/shared/section/typography/paragraph/Paragraph.vue";
import Features from "@/views/blocks/shared/section/features/Features.vue";
import Feature from "@/views/blocks/shared/section/features/Feature.vue";

const props = defineProps<{
    block: PageBlock<SplitWithImageProps>;
    pageColors: CssVarColors;
}>();

const dataValues = computed(() => props.block.dataValues);
const styleValues = computed(() => props.block.styleValues);
</script>

<template>
    <Section :id="block.webId" :backgroundColor="styleValues.background">
        <div class="flex flex-wrap">
            <div class="lg:mr-auto lg:w-1/2 max-h-80 lg:max-h-[1680px] around-image">
                <EditableElement
                    v-if="!dataValues.image.isHidden"
                    :blockId="block.id"
                    fieldName="image"
                >
                    <Image
                        :src="dataValues.image.data.src"
                        :alt="dataValues.image.data.alt"
                        class="lg:mr-auto w-full lg:w-auto h-full object-cover"
                        width="952"
                        height="1482"
                    />
                </EditableElement>
            </div>
            <div
                class="flex flex-col justify-center mx-auto px-6 pb-24 pt-16 lg:px-12 xl:px-16 lg:py-32 lg:w-1/2"
            >
                <div class="max-w-2xl lg:max-w-xl lg:mr-auto">
                    <EditableElement
                        v-if="!dataValues.tag.isHidden"
                        :blockId="block.id"
                        fieldName="tag"
                    >
                        <Heading
                            :text="dataValues.tag.data.text"
                            :tag="dataValues.tag.data.tag"
                            :textColor="styleValues.brand"
                            class="text-base/7 mb-2"
                        />
                    </EditableElement>
                    <EditableElement
                        v-if="!dataValues.title.isHidden"
                        :blockId="block.id"
                        fieldName="title"
                    >
                        <Heading
                            :text="dataValues.title.data.text"
                            :tag="dataValues.title.data.tag"
                            :textColor="styleValues.text"
                            class="text-4xl lg:text-5xl mb-6"
                        />
                    </EditableElement>
                    <EditableElement
                        v-if="!dataValues.description.isHidden"
                        :blockId="block.id"
                        fieldName="description"
                    >
                        <Paragraph
                            :text="dataValues.description.data.text"
                            :textColor="styleValues.text"
                            class="text-xl/8 opacity-70"
                        />
                    </EditableElement>
                    <div class="mt-10 max-w-xl">
                        <EditableElement
                            v-if="!dataValues.topText.isHidden"
                            :blockId="block.id"
                            fieldName="topText"
                        >
                            <Paragraph
                                :text="dataValues.topText.data.text"
                                :textColor="styleValues.text"
                                class="text-base/7 opacity-70"
                            />
                        </EditableElement>
                        <Features class="mt-8 max-w-xl">
                            <EditableElement
                                v-for="feature in dataValues.features.data.items"
                                :key="feature.id"
                                :blockId="block.id"
                                :fieldName="`features_${feature.id}`"
                                :hideable="false"
                            >
                                <Feature
                                    :title="feature.title"
                                    :description="feature.description"
                                    :iconSrc="feature.iconSrc"
                                    :titleTextColor="styleValues.text"
                                    :descriptionTextColor="styleValues.text"
                                />
                            </EditableElement>
                        </Features>
                        <EditableElement
                            v-if="!dataValues.bottomText.isHidden"
                            :blockId="block.id"
                            fieldName="bottomText"
                        >
                            <Paragraph
                                :text="dataValues.bottomText.data.text"
                                :textColor="styleValues.text"
                                class="text-base/7 mt-8 opacity-70"
                            />
                        </EditableElement>
                        <EditableElement
                            v-if="!dataValues.secondaryTitle.isHidden"
                            :blockId="block.id"
                            fieldName="secondaryTitle"
                        >
                            <Heading
                                :text="dataValues.secondaryTitle.data.text"
                                :tag="dataValues.secondaryTitle.data.tag"
                                :textColor="styleValues.text"
                                class="text-2xl mt-16 font-bold"
                            />
                        </EditableElement>
                        <EditableElement
                            v-if="!dataValues.secondaryDescription.isHidden"
                            :blockId="block.id"
                            fieldName="secondaryDescription"
                        >
                            <Paragraph
                                :text="dataValues.secondaryDescription.data.text"
                                :textColor="styleValues.text"
                                class="text-base/7 mt-6 opacity-70"
                            />
                        </EditableElement>
                    </div>
                </div>
            </div>
        </div>
    </Section>
</template>

<style scoped>
@media (min-width: 1024px) {
    .around-image {
        max-height: 1680px;
    }
}
</style>
